import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../lib/auth';
import { haptic } from '../../lib/haptic';

type Props = { onClose: () => void };

type WheelItem = {
  key: string;
  to: string;
  labelKey: string;
  icon: JSX.Element;
  disabled?: boolean;
};

// 7 wedges to match the v2 design's geometry exactly (360 / 7 = 51.4286°).
// "today" duplicates "/" for now — there's no separate /today route in PR-2.
const WEDGE_COUNT = 7;
const STEP = 360 / WEDGE_COUNT;
const SELECTOR_OFFSET = 1; // active wedge sits 1 step clockwise from top, aligned with the FleetUp logo arrow.
const TAP_THRESHOLD = 0.5; // degrees of pointer movement before treating as drag
// Navigation pacing. Both paths show the "Opening…" message before the
// route change. Tap is direct, snap waits for the spring-settle animation
// to finish first so the user clearly sees where the wheel landed.
const TAP_NAVIGATE_DELAY = 700; // ms — long enough that the "Opening…" caption registers on tap
const SNAP_NAVIGATE_DELAY = 950; // ms — covers the 380ms snap animation + ~570ms readable pause

// Lifted verbatim from docs/superpowers/designs/2026-05-16-driver-menu-v2.html.
// Wedge geometry inside a 360x360 viewBox, outer R=160, inner R=65, ~51.43° arc.
const WEDGE_PATH = 'M 324.15 110.58 A 160 160 0 0 1 324.15 249.42 L 238.56 208.20 A 65 65 0 0 0 238.56 151.80 Z';

// Label positions copied from the v2 design HTML (percent-based, inside the rotating layer).
const LABEL_POSITIONS: Record<string, { left: string; top: string }> = {
  home: { left: '50.00%', top: '18.75%' },
  today: { left: '74.43%', top: '30.52%' },
  history: { left: '80.47%', top: '56.95%' },
  vehicle: { left: '63.56%', top: '78.16%' },
  profile: { left: '36.44%', top: '78.16%' },
  language: { left: '19.53%', top: '56.95%' },
  help: { left: '25.57%', top: '30.52%' },
};

const HomeIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 9.5L12 2l9 7.5V21a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9.5z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);
const TodayIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const HistoryIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);
const VehicleIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="11" width="20" height="7" rx="1" />
    <path d="M5 11V8a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v3" />
    <circle cx="7" cy="18" r="1.5" />
    <circle cx="17" cy="18" r="1.5" />
  </svg>
);
const ProfileIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
// Material Symbols "translate (indic)" — filled glyph, tinted via currentColor
// to match the other wheel icons (green when active, white otherwise).
const LanguageIcon = (
  <svg viewBox="0 -960 960 960" fill="currentColor" aria-hidden="true">
    <path d="m476-80 182-480h84L924-80h-84l-43-122H603L560-80h-84Zm152-192h144l-72-204-72 204Zm-374-48q-66 0-123.5-38.5T44-462l72-36q21 42 58 70t79 28q38 0 62.5-23.5T340-480q0-33-23.5-56.5T260-560h-60v-80h60q25 0 42.5-17.5T320-700q0-25-17-42.5T261-760q-23 0-41 15t-32 33l-63-49q26-32 60-55.5t77-23.5q57 0 97.5 40.5T400-701q0 27-10 52.5T361-603q10 10 18.5 20.5T396-560h124v-200h-80v-80h240v80h-80v116l-61 164H420v4q0 63-46 109.5T254-320Z" />
  </svg>
);
const HelpIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

// FleetUp logo SVG — center hub. Lifted from v2 design HTML.
const FleetUpLogo = (
  <svg viewBox="0 0 1315.5 739.5" xmlns="http://www.w3.org/2000/svg" aria-label="FleetUp">
    <defs>
      <clipPath id="mw-lg-bg"><path d="M 137 0 L 1314.8 0 L 1314.8 630 L 137 630 Z" /></clipPath>
      <clipPath id="mw-lg-bound"><rect x="0" y="0" width="1178" height="630" /></clipPath>
      <clipPath id="mw-lg-arrow1"><path d="M 851 132 L 1137 132 L 1137 424 L 851 424 Z" /></clipPath>
      <clipPath id="mw-lg-arrow2"><path d="M 977.66 450.77 L 851.47 288.67 L 1051.63 132.84 L 1177.82 294.94 Z" /></clipPath>
      <clipPath id="mw-lg-arrow3"><path d="M 764.48 616.72 L 444.06 205.14 L 857.06 -116.38 L 1177.48 295.20 Z" /></clipPath>
    </defs>
    <g clipPath="url(#mw-lg-bg)">
      <g transform="translate(137 0)">
        <g clipPath="url(#mw-lg-bound)">
          <g fill="#1A1A1A">
            <g transform="translate(1.76 520.37)"><path d="M 121.08 -277.25 C 98.55 -277.25 87.28 -264.98 87.28 -240.45 L 87.28 -226.77 L 150.17 -226.77 L 150.17 -183.98 L 88.56 -183.98 L 88.56 0 L 35.08 0 L 35.08 -241.31 C 35.08 -265.56 42.21 -284.82 56.47 -299.08 C 70.74 -313.34 90.99 -320.47 117.23 -320.47 C 137.48 -320.47 153.46 -316.33 165.16 -308.06 L 150.17 -267.84 C 141.62 -274.11 131.92 -277.25 121.08 -277.25 Z" /></g>
            <g transform="translate(148.08 520.37)"><path d="M 110.39 3 C 86.71 3 68.24 -3.49 54.97 -16.47 C 41.71 -29.45 35.08 -47.77 35.08 -71.45 L 35.08 -317.47 L 88.56 -317.47 L 88.56 -75.30 C 88.56 -52.48 99.26 -41.08 120.66 -41.08 C 127.79 -41.08 134.21 -42.50 139.91 -45.36 L 142.48 -2.56 C 132.49 1.14 121.80 3 110.39 3 Z" /></g>
            <g transform="translate(282.86 520.37)"><path d="M 143.77 -42.36 C 168.58 -42.36 189.25 -50.63 205.80 -67.17 L 234.03 -34.23 C 224.05 -21.96 211.22 -12.69 195.53 -6.41 C 179.84 -0.13 162.16 3 142.48 3 C 117.38 3 95.27 -1.99 76.16 -11.97 C 57.05 -21.96 42.29 -35.86 31.88 -53.69 C 21.47 -71.52 16.27 -91.70 16.27 -114.23 C 16.27 -136.48 21.33 -156.52 31.45 -174.34 C 41.58 -192.18 55.55 -206.08 73.38 -216.06 C 91.21 -226.05 111.39 -231.05 133.92 -231.05 C 155.32 -231.05 174.79 -226.41 192.33 -217.14 C 209.87 -207.87 223.77 -194.60 234.03 -177.34 C 244.30 -160.09 249.44 -139.91 249.44 -116.81 L 74.45 -82.58 C 79.87 -69.45 88.57 -59.47 100.55 -52.63 C 112.52 -45.78 126.93 -42.36 143.77 -42.36 Z M 133.92 -188.27 C 114.23 -188.27 98.33 -181.84 86.20 -169 C 74.09 -156.16 68.03 -138.91 68.03 -117.23 L 68.03 -116.81 L 197.25 -141.63 C 193.54 -155.60 186.05 -166.87 174.78 -175.42 C 163.51 -183.98 149.89 -188.27 133.92 -188.27 Z" /></g>
            <g transform="translate(543.85 520.37)"><path d="M 143.77 -42.36 C 168.58 -42.36 189.25 -50.63 205.80 -67.17 L 234.03 -34.23 C 224.05 -21.96 211.22 -12.69 195.53 -6.41 C 179.84 -0.13 162.16 3 142.48 3 C 117.38 3 95.27 -1.99 76.16 -11.97 C 57.05 -21.96 42.29 -35.86 31.88 -53.69 C 21.47 -71.52 16.27 -91.70 16.27 -114.23 C 16.27 -136.48 21.33 -156.52 31.45 -174.34 C 41.58 -192.18 55.55 -206.08 73.38 -216.06 C 91.21 -226.05 111.39 -231.05 133.92 -231.05 C 155.32 -231.05 174.79 -226.41 192.33 -217.14 C 209.87 -207.87 223.77 -194.60 234.03 -177.34 C 244.30 -160.09 249.44 -139.91 249.44 -116.81 L 74.45 -82.58 C 79.87 -69.45 88.57 -59.47 100.55 -52.63 C 112.52 -45.78 126.93 -42.36 143.77 -42.36 Z M 133.92 -188.27 C 114.23 -188.27 98.33 -181.84 86.20 -169 C 74.09 -156.16 68.03 -138.91 68.03 -117.23 L 68.03 -116.81 L 197.25 -141.63 C 193.54 -155.60 186.05 -166.87 174.78 -175.42 C 163.51 -183.98 149.89 -188.27 133.92 -188.27 Z" /></g>
            <g transform="translate(804.84 520.37)"><path d="M 163.44 -12.41 C 157.16 -7.27 149.54 -3.41 140.55 -0.84 C 131.57 1.72 122.08 3 112.09 3 C 87 3 67.60 -3.56 53.91 -16.69 C 40.22 -29.81 33.38 -48.92 33.38 -74.02 L 33.38 -278.97 L 86.86 -278.97 L 86.86 -226.77 L 148.05 -226.77 L 148.05 -183.98 L 86.86 -183.98 L 86.86 -75.30 C 86.86 -64.17 89.64 -55.69 95.20 -49.84 C 100.77 -44 108.54 -41.08 118.52 -41.08 C 130.49 -41.08 140.48 -44.21 148.47 -50.48 Z" /></g>
          </g>
          <g clipPath="url(#mw-lg-arrow1)">
            <g clipPath="url(#mw-lg-arrow2)">
              <g clipPath="url(#mw-lg-arrow3)">
                <path fill="#2E9E65" d="M 1136.68 243.36 L 945.48 221.42 L 999.21 289.09 C 999.21 289.09 700.81 353.15 679.67 22.39 C 679.67 22.39 610.03 418.29 1011.26 331.64 L 1012.34 423.77 L 1136.68 243.36 Z" />
              </g>
            </g>
          </g>
        </g>
      </g>
    </g>
  </svg>
);

export function MenuWheel({ onClose }: Props) {
  const { t } = useTranslation('menu');
  const nav = useNavigate();
  const signOut = useAuth(s => s.signOut);

  // Items in the same order as the v2 design's <use> elements (KEYS array).
  const items: WheelItem[] = [
    { key: 'home', to: '/', labelKey: 'home', icon: HomeIcon },
    { key: 'today', to: '/today', labelKey: 'today', icon: TodayIcon },
    { key: 'history', to: '/trips/history', labelKey: 'history', icon: HistoryIcon },
    { key: 'vehicle', to: '/vehicle', labelKey: 'vehicle', icon: VehicleIcon },
    { key: 'profile', to: '/profile', labelKey: 'profile', icon: ProfileIcon },
    { key: 'language', to: '/profile/language', labelKey: 'language', icon: LanguageIcon },
    { key: 'help', to: '/help', labelKey: 'help', icon: HelpIcon },
  ];

  // Wedge transforms from v2 design HTML — start at -90 (12 o'clock) then step by STEP.
  // home = -90, today = -38.571, history = 12.857, etc.
  const wedgeRotations = items.map((_, i) => -90 + i * STEP);

  const wheelRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{
    startAngle: number;
    startRotation: number;
    pointerId: number;
    didMove: boolean;
    pressedKey: string | null;
  } | null>(null);

  const [rotation, setRotation] = useState<number>(SELECTOR_OFFSET * STEP);
  const [snapping, setSnapping] = useState(false);
  // `navigating` drives the "Opening …" caption + freezes further input
  // while the route change is pending. Set on tap or after a drag-snap settles.
  const [navigating, setNavigating] = useState<WheelItem | null>(null);
  const snapTimerRef = useRef<number | null>(null);
  const navTimerRef = useRef<number | null>(null);
  // Track active-wedge index across renders so we can fire a detent haptic
  // whenever the wheel rotates past a wedge boundary mid-drag (the "tick tick
  // tick" feel as you scroll a rotary control).
  const lastDetentIdxRef = useRef<number>(SELECTOR_OFFSET);

  useEffect(() => {
    return () => {
      if (snapTimerRef.current !== null) window.clearTimeout(snapTimerRef.current);
      if (navTimerRef.current !== null) window.clearTimeout(navTimerRef.current);
    };
  }, []);

  const cancelPendingNav = useCallback(() => {
    if (navTimerRef.current !== null) {
      window.clearTimeout(navTimerRef.current);
      navTimerRef.current = null;
    }
    setNavigating(null);
  }, []);

  function activeIndex(rot: number): number {
    return ((Math.round(-rot / STEP) + SELECTOR_OFFSET) % WEDGE_COUNT + WEDGE_COUNT) % WEDGE_COUNT;
  }

  const beginSnap = useCallback(() => {
    setSnapping(true);
    if (snapTimerRef.current !== null) window.clearTimeout(snapTimerRef.current);
    snapTimerRef.current = window.setTimeout(() => {
      setSnapping(false);
      snapTimerRef.current = null;
    }, 380);
  }, []);

  function pointerAngle(e: React.PointerEvent | PointerEvent): number {
    const el = wheelRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    return Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI;
  }

  function rotateToIndex(idx: number) {
    let target = (SELECTOR_OFFSET - idx) * STEP;
    // Normalise to the shortest spin from current rotation.
    setRotation(prev => {
      let t = target;
      while (t - prev > 180) t -= 360;
      while (t - prev < -180) t += 360;
      return t;
    });
    beginSnap();
  }

  function navigateToItem(item: WheelItem, delay: number = TAP_NAVIGATE_DELAY) {
    if (item.disabled) return; // PR-3 wiring blocker: disabled wedges (e.g. "today") no-op.
    if (navTimerRef.current !== null) return;
    haptic.select();
    setNavigating(item);
    navTimerRef.current = window.setTimeout(() => {
      navTimerRef.current = null;
      nav(item.to);
      onClose();
    }, delay);
  }

  function onPointerDown(e: React.PointerEvent) {
    e.preventDefault();
    // If a previous selection's "Opening …" countdown was running, the user
    // touching the wheel again means they changed their mind. Cancel it.
    cancelPendingNav();
    const target = (e.target as HTMLElement).closest('[data-wedge-key]') as HTMLElement | null;
    const pressedKey = target?.dataset.wedgeKey ?? null;
    const startAngle = pointerAngle(e);
    dragRef.current = {
      startAngle,
      startRotation: rotation,
      pointerId: e.pointerId,
      didMove: false,
      pressedKey,
    };
    setSnapping(false);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent) {
    const d = dragRef.current;
    if (!d) return;
    e.preventDefault();
    const a = pointerAngle(e);
    const delta = a - d.startAngle;
    if (Math.abs(delta) > TAP_THRESHOLD) d.didMove = true;
    const nextRotation = d.startRotation + delta;
    setRotation(nextRotation);
    // Fire a micro haptic each time the active wedge changes mid-drag.
    const detent = activeIndex(nextRotation);
    if (detent !== lastDetentIdxRef.current) {
      lastDetentIdxRef.current = detent;
      haptic.snap();
    }
  }

  function onPointerUp(e: React.PointerEvent) {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(d.pointerId);
    } catch { /* ignore */ }

    if (!d.didMove && d.pressedKey) {
      // Tap — rotate the pressed wedge to the selector then navigate.
      const idx = items.findIndex(it => it.key === d.pressedKey);
      const tapped = idx >= 0 ? items[idx] : undefined;
      if (idx >= 0 && tapped && !tapped.disabled) {
        rotateToIndex(idx);
        navigateToItem(tapped, TAP_NAVIGATE_DELAY);
      }
    } else {
      // Drag — snap to nearest wedge boundary AND navigate to whatever the
      // wheel lands on. The longer SNAP_NAVIGATE_DELAY (~950ms) covers the
      // spring-settle animation so the user clearly reads where it landed
      // before the route changes. A new touch cancels the pending nav.
      const snapped = Math.round(rotation / STEP) * STEP;
      setRotation(snapped);
      beginSnap();
      haptic.snap();
      const landedIdx = activeIndex(snapped);
      const landed = items[landedIdx];
      if (landed && !landed.disabled) {
        navigateToItem(landed, SNAP_NAVIGATE_DELAY);
      }
    }
  }

  async function handleSignOut() {
    haptic.select();
    await signOut();
    onClose();
  }

  const currentIdx = activeIndex(rotation);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        width: '100%',
        maxWidth: '440px',
        margin: '0 auto',
        padding: '8px 18px 24px',
      }}
    >
      {/* Wheel container */}
      <div
        ref={wheelRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        role="group"
        aria-label="Menu wheel — tap an item to navigate"
        style={{
          position: 'relative',
          width: '320px',
          height: '320px',
          maxWidth: '100%',
          aspectRatio: '1 / 1',
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        {/*
          Visually-hidden parallel navigation list. SVG <use> wedges are not
          screen-reader navigable; this <ul> mirrors the same 7 items so
          keyboard + AT users get the same destinations via Tab/Enter.
        */}
        <ul
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {items.map(item => (
            <li key={item.key}>
              <button
                type="button"
                onClick={() => navigateToItem(item)}
                disabled={item.disabled}
              >
                {t(item.labelKey)}
              </button>
            </li>
          ))}
        </ul>
        {/* Glassmorphic backdrop circle behind the wedges — single HTML layer. */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.04)',
            backdropFilter: 'blur(24px) saturate(1.5)',
            WebkitBackdropFilter: 'blur(24px) saturate(1.5)',
            pointerEvents: 'none',
            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.10)',
          }}
        />

        {/* Rotating layer: SVG wedges + counter-rotating labels */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            transformOrigin: 'center',
            transform: `rotate(${rotation}deg)`,
            transition: snapping ? 'transform 360ms cubic-bezier(0.2, 0.8, 0.2, 1)' : 'none',
          }}
        >
          <svg
            viewBox="0 0 360 360"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              filter: 'drop-shadow(0 18px 36px rgba(15, 59, 38, 0.10))',
            }}
          >
            <defs>
              <path id="mwWedgeShape" d={WEDGE_PATH} />
            </defs>
            {items.map((item, idx) => {
              const isActive = idx === currentIdx;
              const isDisabled = !!item.disabled;
              return (
                <use
                  key={item.key}
                  href="#mwWedgeShape"
                  data-wedge-key={item.key}
                  transform={`rotate(${wedgeRotations[idx]} 180 180)`}
                  style={{
                    fill: isActive ? '#2E9E65' : 'rgba(255, 255, 255, 0.16)',
                    fillOpacity: isDisabled ? 0.5 : 1,
                    stroke: isActive ? 'rgba(255, 255, 255, 0.40)' : 'rgba(255, 255, 255, 0.30)',
                    strokeWidth: 1.5,
                    cursor: isDisabled ? 'not-allowed' : 'grab',
                    transition: 'fill 240ms ease, stroke 240ms ease',
                  }}
                />
              );
            })}
            <circle cx="180" cy="180" r="160" fill="none" stroke="rgba(255, 255, 255, 0.20)" strokeWidth="1" opacity="0.6" />
            <circle cx="180" cy="180" r="65" fill="white" stroke="rgba(255, 255, 255, 0.30)" strokeWidth="1.5" />
          </svg>

          {items.map((item, idx) => {
            const pos = LABEL_POSITIONS[item.key];
            if (!pos) return null;
            const isActive = idx === currentIdx;
            const isDisabled = !!item.disabled;
            return (
              <div
                key={item.key}
                data-wedge-key={item.key}
                style={{
                  position: 'absolute',
                  left: pos.left,
                  top: pos.top,
                  transform: `translate(-50%, -50%) rotate(${-rotation}deg)`,
                  transformOrigin: 'center',
                  transition: snapping ? 'transform 360ms cubic-bezier(0.2, 0.8, 0.2, 1)' : 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  width: '72px',
                  textAlign: 'center',
                  pointerEvents: 'none',
                  userSelect: 'none',
                  opacity: isDisabled ? 0.5 : 1,
                }}
              >
                <span
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '999px',
                    background: isActive ? 'white' : 'rgba(255, 255, 255, 0.12)',
                    border: `1px solid ${isActive ? 'rgba(255, 255, 255, 0.40)' : 'rgba(255, 255, 255, 0.15)'}`,
                    color: isDisabled
                      ? 'rgba(255, 255, 255, 0.55)'
                      : isActive
                        ? 'var(--green-700)'
                        : 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: isActive ? '0 6px 16px rgba(255, 255, 255, 0.18)' : '0 2px 8px rgba(0, 0, 0, 0.25)',
                    transition: 'background 240ms ease, color 240ms ease, border-color 240ms ease, box-shadow 240ms ease',
                  }}
                >
                  <span style={{ width: '17px', height: '17px', display: 'block' }}>{item.icon}</span>
                </span>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: isActive ? 800 : 700,
                    color: isDisabled
                      ? 'rgba(255, 255, 255, 0.55)'
                      : isActive
                        ? 'white'
                        : 'rgba(255, 255, 255, 0.92)',
                    letterSpacing: '-0.01em',
                    lineHeight: 1.2,
                    transition: 'color 240ms ease',
                  }}
                >
                  {t(item.labelKey)}
                </span>
              </div>
            );
          })}
        </div>

        {/* Center hub — FleetUp logo. Does NOT rotate. */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '116px',
            height: '116px',
            borderRadius: '999px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 5,
          }}
        >
          <div style={{ width: '76px', height: 'auto' }}>{FleetUpLogo}</div>
        </div>
      </div>

      {/* "Opening …" caption — reserves height so the layout doesn't jump
          when the message appears. Aria-live so screen readers announce. */}
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          minHeight: '24px',
          marginTop: '8px',
          fontSize: '14px',
          fontWeight: 600,
          letterSpacing: '-0.01em',
          color: 'rgba(255, 255, 255, 0.85)',
          textAlign: 'center',
          opacity: navigating ? 1 : 0,
          transform: navigating ? 'translateY(0)' : 'translateY(4px)',
          transition: 'opacity 200ms ease, transform 200ms ease',
        }}
      >
        {navigating ? (
          <>
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                width: '8px',
                height: '8px',
                borderRadius: '999px',
                background: 'rgba(255, 255, 255, 0.85)',
                marginRight: '8px',
                verticalAlign: 'middle',
                animation: 'mwOpeningPulse 900ms ease-in-out infinite',
              }}
            />
            {t('opening', { label: t(navigating.labelKey) })}
          </>
        ) : (
          // Zero-width placeholder preserves the height before the first selection
          ' '
        )}
      </div>
      {/* Pulse keyframes — scoped to this component via a unique name. */}
      <style>{`
        @keyframes mwOpeningPulse {
          0%, 100% { opacity: 0.35; transform: scale(0.9); }
          50%      { opacity: 1;    transform: scale(1.1); }
        }
      `}</style>

      {/* Sign out — white-ish, less opaque (NOT red) */}
      <button
        type="button"
        onClick={handleSignOut}
        style={{
          width: '100%',
          maxWidth: '280px',
          marginTop: '14px',
          height: '48px',
          border: '1.5px solid rgba(255, 255, 255, 0.22)',
          borderRadius: 'var(--radius-pill)',
          background: 'transparent',
          color: 'rgba(255, 255, 255, 0.75)',
          fontFamily: 'inherit',
          fontSize: '14px',
          fontWeight: 700,
          letterSpacing: '0.01em',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {t('signOut')}
      </button>
    </div>
  );
}
